import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { defaultFilters } from '../defaultFilters';
import { useHiresSearchParam } from './useHiresSearchParam';
import { activeNetworkSelector } from '../../../redux/selectors';
import { trackEvent } from '../../../helpers/analytics';

export const useHiresFilters = () => {
  const [queryParams, setQueryParams] = useHiresSearchParam({ filters: defaultFilters });
  const activeNetwork = useSelector(activeNetworkSelector);

  const { filters } = queryParams;
  const keyword = filters.keyword || '';

  const setFilters = useCallback(
    (newFilters) => {
      // Track filter changes
      Object.keys(newFilters).forEach((filterName) => {
        const filterValue = newFilters[filterName];
        // Extract just the value for tracking
        let selectedValues = filterValue;
        if (Array.isArray(filterValue)) {
          selectedValues = filterValue.map((item) => item?.value ?? item);
        } else if (filterValue && typeof filterValue === 'object' && 'value' in filterValue) {
          selectedValues = filterValue.value;
        }

        trackEvent('hires:filter_change', {
          collection_id: activeNetwork?.id,
          filter_name: filterName,
          selected_values: selectedValues,
        });
      });

      setQueryParams({
        ...queryParams,
        filters: { ...filters, ...newFilters },
        page: 1,
      });
    },
    [queryParams, filters, setQueryParams, activeNetwork?.id],
  );

  const setMultipleFilters = useCallback(
    (group, options = {}) => {
      const { removeFilters = [] } = options;
      const cleanFilters = Object.keys(group).reduce((arr, key) => {
        if (group[key]?.value === 'any' || group[key]?.value === null) {
          removeFilters.push(key);
          return arr;
        }
        arr[key] = group[key]; // eslint-disable-line no-param-reassign
        return arr;
      }, {});

      const newFilters = options?.resetPreviousFilters
        ? { ...defaultFilters, ...cleanFilters }
        : { ...filters, ...cleanFilters };

      [...new Set(removeFilters)].forEach((item) => {
        delete newFilters[item];
      });

      setQueryParams({
        ...queryParams,
        filters: newFilters,
        page: 1,
      });
    },
    [queryParams, filters, setQueryParams],
  );

  const resetFilters = useCallback(() => {
    setQueryParams({
      ...queryParams,
      filters: { ...defaultFilters },
      page: 1,
    });
  }, [queryParams, setQueryParams]);

  const handleKeywordChange = useCallback(
    (value) => {
      trackEvent('hires:filter_change', {
        collection_id: activeNetwork?.id,
        filter_name: 'keyword',
        selected_values: value || '',
      });

      setQueryParams({
        ...queryParams,
        filters: { ...filters, keyword: value || '' },
        page: 1,
      });
    },
    [queryParams, filters, setQueryParams, activeNetwork?.id],
  );

  const filtersCount = useMemo(() => {
    let count = 0;
    Object.keys(filters).forEach((key) => {
      // Skip sortBy, sortDirection, and keyword from count
      if (key === 'sortBy' || key === 'sortDirection' || key === 'keyword') return;

      const value = filters[key];
      const defaultValue = defaultFilters[key];

      // Skip null, undefined, empty values
      if (value === null || value === undefined) return;

      // Skip if value equals default
      if (value === defaultValue) return;

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length > 0) count += 1;
        return;
      }

      // Handle objects (e.g., { value: 'any' } or { startDate, endDate })
      if (typeof value === 'object') {
        // Skip if object has value property that is 'any' or empty
        if (value.value === 'any' || value.value === '' || value.value === null) return;
        // Count if object has meaningful data (like hireDate with startDate/endDate)
        if (Object.keys(value).length > 0) count += 1;
        return;
      }

      // Skip empty strings and 'any'
      if (value === '' || value === 'any') return;

      // Count everything else
      count += 1;
    });

    // Count keyword separately
    if (keyword && keyword.length > 0) {
      count += 1;
    }

    return count;
  }, [filters, keyword]);

  return {
    filters,
    setFilters,
    setMultipleFilters,
    keyword,
    handleKeywordChange,
    resetFilters,
    filtersCount,
    queryParams,
    setQueryParams,
  };
};
